import { SaveManager } from '../../../../../state/SaveManager.js';
import type { CharacterGroveConfig, CharacterGroveHint } from './CharacterGroveConfig.js';
import {
  CharacterGroveState,
  type CharacterGroveConnectionResult,
  type CharacterGroveSnapshot,
} from './CharacterGroveState.js';

export type CharacterGroveLayoutMode = 'standard' | 'compact';

export interface CharacterGroveVisibleBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export interface CharacterGroveChallengeEvents {
  readonly onStateChanged?: (snapshot: CharacterGroveSnapshot) => void;
  readonly onHint?: (hint: CharacterGroveHint, snapshot: CharacterGroveSnapshot) => void;
  readonly onIncorrect?: (
    result: Exclude<CharacterGroveConnectionResult, 'incomplete' | 'correct'>,
    clue: string,
    snapshot: CharacterGroveSnapshot,
  ) => void;
  readonly onCompleted?: (
    config: CharacterGroveConfig,
    snapshot: CharacterGroveSnapshot,
  ) => void;
}

/**
 * Rendering-independent Memory Branches controller. Pointer, touch, and
 * keyboard views call the same methods; relayout changes only layoutMode.
 */
export class CharacterGroveChallenge {
  readonly state: CharacterGroveState;
  private completionEmitted = false;
  private layoutMode: CharacterGroveLayoutMode = 'standard';
  private suspended = false;

  constructor(
    readonly config: CharacterGroveConfig,
    private readonly events: CharacterGroveChallengeEvents = {},
    initialSnapshot?: CharacterGroveSnapshot,
  ) {
    this.state = new CharacterGroveState(config, initialSnapshot);
    this.completionEmitted = this.state.isComplete;
  }

  getSnapshot() { return this.state.snapshot(); }
  get currentLayoutMode() { return this.layoutMode; }

  setStoryPage(index: number) { return this.change(() => this.state.setStoryPage(index)); }
  nextStoryPage() { return this.change(() => this.state.nextStoryPage()); }
  previousStoryPage() { return this.change(() => this.state.previousStoryPage()); }
  finishReading() { return this.change(() => this.state.finishReading()); }
  inspectCharacter() { return this.change(() => this.state.inspectCharacter()); }
  selectInsight(insightId: string) {
    return this.change(() => this.state.selectInsight(insightId));
  }
  selectEvidence(evidenceId: string) {
    return this.change(() => this.state.selectEvidence(evidenceId));
  }

  connect() {
    if (this.suspended) return 'incomplete' as const;
    const result = this.state.connect();
    const snapshot = this.state.snapshot();
    if (result !== 'incomplete') SaveManager.recordAnswer(this.config.skillId, result === 'correct');
    if (result === 'insight-mismatch' || result === 'evidence-mismatch') {
      const clue = result === 'insight-mismatch'
        ? 'What did the character do when the problem happened?'
        : 'Look for the moment that shows how the character responded.';
      this.events.onIncorrect?.(result, clue, snapshot);
    }
    this.events.onStateChanged?.(snapshot);
    if (result === 'correct' && !this.completionEmitted) {
      this.completionEmitted = true;
      this.events.onCompleted?.(this.config, snapshot);
    }
    return result;
  }

  requestHint() {
    if (this.suspended || this.state.isComplete) return undefined;
    const hint = this.state.requestHint();
    const snapshot = this.state.snapshot();
    this.events.onHint?.(hint, snapshot);
    this.events.onStateChanged?.(snapshot);
    return hint;
  }

  reset() {
    if (this.suspended) return false;
    const changed = this.state.reset();
    this.completionEmitted = false;
    if (changed) this.publish();
    return changed;
  }

  layout(bounds: CharacterGroveVisibleBounds) {
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;
    this.layoutMode = width < 1100 || height < 640 ? 'compact' : 'standard';
    return this.layoutMode;
  }

  setSuspended(suspended: boolean) { this.suspended = suspended; }

  private change(operation: () => boolean) {
    if (this.suspended) return false;
    const changed = operation();
    if (changed) this.publish();
    return changed;
  }

  private publish() { this.events.onStateChanged?.(this.state.snapshot()); }
}
